import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  ArrowLeft, 
  Users,
  Home,
  Target,
  Calendar,
  CheckCircle,
  XCircle,
  TrendingUp,
  Shuffle,
  Save,
  User
} from "lucide-react";

export default function AttendancePage({ evaluator }) {
  const navigate = useNavigate();
  const [gamedays, setGamedays] = useState([]);
  const [selectedGameday, setSelectedGameday] = useState(null);
  const [attendance, setAttendance] = useState({});
  const [stats, setStats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [view, setView] = useState("register"); // register or stats

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedGameday) {
      fetchAttendanceForGameday(selectedGameday.id);
    }
  }, [selectedGameday]);

  const fetchData = async () => {
    try {
      const [gamedaysRes, statsRes] = await Promise.all([
        axios.get(`${API}/gamedays`),
        axios.get(`${API}/attendance/stats`)
      ]);
      setGamedays(gamedaysRes.data);
      setStats(statsRes.data);
      if (gamedaysRes.data.length > 0) {
        setSelectedGameday(gamedaysRes.data[0]);
      }
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAttendanceForGameday = async (gamedayId) => {
    try {
      const res = await axios.get(`${API}/attendance/gameday/${gamedayId}`);
      const records = res.data.records || [];
      const attendanceMap = {};
      records.forEach(r => {
        attendanceMap[r.player_id] = r.present;
      });
      setAttendance(attendanceMap);
    } catch (error) {
      setAttendance({});
    }
  };

  const handleToggleAttendance = (playerId) => {
    setAttendance(prev => ({
      ...prev,
      [playerId]: !prev[playerId]
    }));
  };

  const handleSaveAttendance = async () => {
    if (!selectedGameday) return;
    
    setIsSaving(true);
    try {
      const playerIds = selectedGameday.player_ids || [];
      const records = playerIds.map(pid => ({
        player_id: pid,
        present: !!attendance[pid]
      }));

      await axios.post(`${API}/attendance`, {
        gameday_id: selectedGameday.id,
        date: selectedGameday.date,
        records
      });
      toast.success("Frequência salva com sucesso!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao salvar frequência");
    } finally {
      setIsSaving(false);
    }
  };

  const getFrequencyColor = (rate) => {
    if (rate >= 80) return "text-green-400";
    if (rate >= 50) return "text-yellow-400";
    return "text-red-400";
  };

  const getFrequencyBadge = (rate) => {
    if (rate >= 80) return "default";
    if (rate >= 50) return "secondary";
    return "destructive";
  };

  const canEdit = evaluator?.role === 'admin' || evaluator?.role === 'tecnico';

  // Get players from selected gameday teams
  const getPlayersFromGameday = () => {
    if (!selectedGameday?.generated_teams) return [];
    const players = [];
    selectedGameday.generated_teams.forEach(team => {
      team.players.forEach(p => {
        players.push({
          id: p.id,
          name: p.name,
          position: p.position,
          team_number: team.team_number
        });
      });
    });
    return players;
  };

  const players = getPlayersFromGameday();
  const presentCount = Object.values(attendance).filter(Boolean).length;
  const totalPlayers = players.length;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight">Frequência</h1>
                <p className="text-sm text-muted-foreground">Monitoramento de presença dos jogadores</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={view === "register" ? "default" : "outline"}
                size="sm"
                onClick={() => setView("register")}
              >
                Registrar
              </Button>
              <Button
                variant={view === "stats" ? "default" : "outline"}
                size="sm"
                onClick={() => setView("stats")}
              >
                Estatísticas
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 content-with-nav">
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Carregando...</div>
        ) : view === "register" ? (
          <div className="space-y-6">
            {/* Gameday Selector */}
            <Card className="bg-card border-border/50 shadow-xl">
              <CardHeader>
                <CardTitle className="text-lg font-bold flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  Selecionar Jogo
                </CardTitle>
              </CardHeader>
              <CardContent>
                {gamedays.length === 0 ? (
                  <p className="text-muted-foreground">Nenhum jogo cadastrado. Gere times na página "Times do Dia" primeiro.</p>
                ) : (
                  <Select
                    value={selectedGameday?.id || ""}
                    onValueChange={(v) => setSelectedGameday(gamedays.find(g => g.id === v))}
                  >
                    <SelectTrigger className="bg-input/50">
                      <SelectValue placeholder="Selecione o jogo" />
                    </SelectTrigger>
                    <SelectContent>
                      {gamedays.map(gd => (
                        <SelectItem key={gd.id} value={gd.id}>
                          {gd.date} - 3 Times ({gd.player_ids?.length || 18} jogadores)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </CardContent>
            </Card>

            {/* Attendance Register */}
            {selectedGameday && players.length > 0 && (
              <Card className="bg-card border-border/50 shadow-xl">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg font-bold flex items-center gap-2">
                        <Users className="h-5 w-5 text-primary" />
                        Registro de Frequência - {selectedGameday.date}
                      </CardTitle>
                      <CardDescription>
                        {presentCount}/{totalPlayers} presentes ({totalPlayers > 0 ? Math.round(presentCount / totalPlayers * 100) : 0}%)
                      </CardDescription>
                    </div>
                    {canEdit && (
                      <Button onClick={handleSaveAttendance} disabled={isSaving}>
                        <Save className="mr-2 h-4 w-4" />
                        {isSaving ? "Salvando..." : "Salvar"}
                      </Button>
                    )}
                  </div>
                  <Progress value={totalPlayers > 0 ? (presentCount / totalPlayers) * 100 : 0} className="mt-2" />
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[1, 2, 3].map(teamNum => {
                      const teamPlayers = players.filter(p => p.team_number === teamNum);
                      const teamPresent = teamPlayers.filter(p => attendance[p.id]).length;
                      const teamColors = ['border-blue-500', 'border-green-500', 'border-purple-500'];
                      
                      return (
                        <Card key={teamNum} className={`border-2 ${teamColors[teamNum - 1]}`}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base">
                              Time {teamNum}
                              <Badge variant="outline" className="ml-2">{teamPresent}/{teamPlayers.length}</Badge>
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {teamPlayers.map(player => (
                                <div
                                  key={player.id}
                                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                                    attendance[player.id] ? 'bg-green-500/20' : 'bg-secondary/30 hover:bg-secondary/50'
                                  }`}
                                  onClick={() => canEdit && handleToggleAttendance(player.id)}
                                >
                                  <Checkbox
                                    checked={!!attendance[player.id]}
                                    className="pointer-events-none"
                                  />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium">{player.name}</p>
                                    <Badge variant="outline" className="text-xs">{player.position}</Badge>
                                  </div>
                                  {attendance[player.id] ? (
                                    <CheckCircle className="h-4 w-4 text-green-400" />
                                  ) : (
                                    <XCircle className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          /* Statistics View */
          <Card className="bg-card border-border/50 shadow-xl">
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Estatísticas de Frequência
              </CardTitle>
              <CardDescription>
                Taxa de presença de todos os jogadores
              </CardDescription>
            </CardHeader>
            <CardContent>
              {stats.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">Nenhum registro de frequência ainda.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead>Jogador</TableHead>
                      <TableHead>Posição</TableHead>
                      <TableHead className="text-center">Total</TableHead>
                      <TableHead className="text-center">Presenças</TableHead>
                      <TableHead className="text-center">Taxa</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.map((stat, index) => (
                      <TableRow key={stat.player_id} className="border-border/50">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="h-4 w-4 text-primary" />
                            </div>
                            {stat.player_name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{stat.position}</Badge>
                        </TableCell>
                        <TableCell className="text-center">{stat.total_games}</TableCell>
                        <TableCell className="text-center">{stat.present_count}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant={getFrequencyBadge(stat.frequency_rate)}>
                            <span className={getFrequencyColor(stat.frequency_rate)}>
                              {stat.frequency_rate}%
                            </span>
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Mobile Navigation */}
      <nav className="mobile-nav">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="flex flex-col items-center gap-1 h-auto py-2">
          <Home className="h-5 w-5" />
          <span className="text-xs">Início</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate('/attendance')} className="flex flex-col items-center gap-1 h-auto py-2 text-primary">
          <CheckCircle className="h-5 w-5" />
          <span className="text-xs">Frequência</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate('/gameday')} className="flex flex-col items-center gap-1 h-auto py-2">
          <Shuffle className="h-5 w-5" />
          <span className="text-xs">Times</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate('/evaluate')} className="flex flex-col items-center gap-1 h-auto py-2">
          <Target className="h-5 w-5" />
          <span className="text-xs">Avaliar</span>
        </Button>
      </nav>
    </div>
  );
}
